/**
 * Pure logic of the changes that give or take away the manager or the super admin role (#326):
 * one super admin proposes, a different one confirms with their code, and the person a change
 * names never decides on it. The backend says each rule again (403 / 409) and so does the
 * database; this only lets the page say it before a click.
 */
import type { StaffChange, StaffChangeKind, UserRole } from '../../services/admin.service';

const PRIVILEGED: ReadonlySet<UserRole> = new Set<UserRole>(['manager', 'super_admin']);

/** Does a change that involves these roles (held, given or taken away) take two super admins? */
export function needsSecondAdmin(...roles: ReadonlyArray<UserRole | null | undefined>): boolean {
  return roles.some((role) => role != null && PRIVILEGED.has(role));
}

export const KIND_LABEL: Record<StaffChangeKind, string> = {
  create: 'New account',
  deactivate: 'Deactivate',
  reactivate: 'Reactivate',
  change_role: 'Change role',
};

/** Who the change is about: the new person's name, or the account's. */
export function changeSubjectName(change: StaffChange): string {
  if (change.kind === 'create') {
    return `${change.first_name ?? ''} ${change.last_name ?? ''}`.trim();
  }
  return change.target_name ?? '';
}

export function changeSubjectEmail(change: StaffChange): string {
  return (change.kind === 'create' ? change.email : change.target_email) ?? '';
}

/** Why this super admin cannot decide on the change, or null when they can confirm it. */
export type DecisionBlock = 'proposer' | 'named';

export function decisionBlock(change: StaffChange): DecisionBlock | null {
  if (change.about_you) return 'named';
  if (change.you_proposed) return 'proposer';
  return null;
}

export const DECISION_BLOCK_MESSAGE: Record<DecisionBlock, string> = {
  proposer: 'You proposed this change. A different super admin confirms it.',
  named: 'This change is about your account. Other super admins decide on it.',
};

/** The proposer may withdraw their own proposal; the person named may not touch it. */
export function canCancel(change: StaffChange): boolean {
  return !change.about_you;
}
